import type { List as ImmutableList, Map as ImmutableMap } from 'immutable'
import moment from 'moment'

import { createDisabledSlotId } from 'routes/Menu/selectors/boxSummary'

import { getLandingDay } from './deliveries'
import { formatDeliveryPrice } from './deliveryPrice'
import { formatDeliveryTime } from './deliverySlot'
import type { UserOrder } from './types/order'

export type ImmutableDeliveryDays = ImmutableMap<string, any>
export type ImmutableSlot = ImmutableMap<string, any>
type ImmutableState = {
  boxSummaryDeliveryDays: ImmutableMap<string, any>
  temp: ImmutableMap<string, any>
}
export type DeliveryDaysProps = {
  disabledSlots: (
    | {
        date: string
        value: string
        disabled: boolean
        label: string
      }
    | string
  )[]
  isSubscriptionActive?: string
  isAuthenticated: boolean
  tempDate: string
  userOrders: ImmutableList<UserOrder>
  tempSlotId: string
  deliveryDaysProps: ImmutableList<ImmutableDeliveryDays> | ImmutableDeliveryDays
}

/**
 * `addDisabledSlotIds` uses `set` so it will change
 * the resource provided to it and add the key `disabledSlotId`.
 */
// TODO: move to selector folder.
export const addDisabledSlotIds = (deliveryDays: ImmutableDeliveryDays) =>
  deliveryDays.map((deliveryDay) => {
    const date = deliveryDay?.get('date')
    const slots = deliveryDay?.get('slots')

    const formattedSlots = slots.map((slot: ImmutableSlot) =>
      slot.set('disabledSlotId', createDisabledSlotId(slot, date)),
    )

    return deliveryDay?.set('slots', formattedSlots)
  }) as ImmutableDeliveryDays

// This method is never tested.
export const getTempDeliveryOptions = (state: ImmutableState) => {
  const deliveryDays = addDisabledSlotIds(state.boxSummaryDeliveryDays)

  const landing = getLandingDay(state, {
    useCurrentSlot: true,
    useBasketDate: false,
  })

  const tempDate = state.temp.get('date', landing.date)
  const tempSlotId = state.temp.get('slotId', landing.slotId)
  const tempOrderId = state.temp.get('orderId')

  return { deliveryDays, tempDate, tempSlotId, tempOrderId }
}

export const getDeliveryDayOrder = (date: string, userOrders: ImmutableList<UserOrder>) => {
  const firstOrder = userOrders
    .toArray()
    .find((order) => moment(date).isSame(moment(order.get('deliveryDate'))))

  return firstOrder
    ? {
        hasOrdersToday: true,
        icon: firstOrder.get('recipeItems')?.size === 0 ? 'empty-box' : 'full-box',
        orderId: firstOrder.get('id'),
      }
    : {
        hasOrdersToday: false,
        icon: null,
        orderId: null,
      }
}

type Slot = {
  label: string
  subLabel: string
  value: string
  coreSlotId: string
  disabled: boolean
}

export const getDeliveryDaysAndSlots = (newDate: string, props: DeliveryDaysProps) => {
  const slots: Record<string, Slot[]> = {}
  const { disabledSlots, isAuthenticated, tempDate, userOrders, tempSlotId, deliveryDaysProps } =
    props

  let hasOrders = false
  let subLabelClassName = ''
  const hasEmptyOrders = userOrders.some((order) => order?.get('recipeItems')?.size === 0)
  const hasFullOrders = userOrders.some((order) => (order?.get('recipeItems')?.size ?? 0) > 0)

  const deliveryDaysData =
    deliveryDaysProps &&
    deliveryDaysProps.map((deliveryDay) => {
      const date = deliveryDay?.get('date')

      slots[date] = deliveryDay
        ?.get('slots')
        .map((slot: ImmutableSlot) => {
          const isSlotDisabled = !!(
            disabledSlots && disabledSlots.includes(slot.get('disabledSlotId') || '')
          )

          return {
            label: formatDeliveryTime(
              slot.get('deliveryStartTime') || '',
              slot.get('deliveryEndTime') || '',
              tempDate,
            ),
            subLabel:
              isAuthenticated && !isSlotDisabled
                ? formatDeliveryPrice(slot.get('deliveryPrice') || '')
                : '',
            value: slot.get('id'),
            coreSlotId: slot.get('coreSlotId'),
            disabled: isSlotDisabled,
          }
        })
        .toArray()

      const { hasOrdersToday, icon, orderId } = getDeliveryDayOrder(date, userOrders)

      if (orderId) hasOrders = true

      // disabled if either are true:
      // * delivery day has an alternate delivery day
      // * all slots on the delivery day are disabled AND user is not authenticated (due to a business decision to treat signup differently than transactional customers)
      let disabled =
        deliveryDay?.get('alternateDeliveryDay') !== null ||
        (slots[date] && slots[date].every((slot) => slot.disabled))
      let legacyData = {}

      if (!isAuthenticated) {
        subLabelClassName = hasOrdersToday ? 'boxIcon' : ''
        legacyData = { label: moment(date).format('ddd D MMM'), ordered: hasOrdersToday }
        disabled = disabled || hasOrdersToday
      }

      return {
        ...legacyData,
        date,
        value: date,
        disabled,
        icon,
        orderId,
      }
    })

  const deliveryDays =
    deliveryDaysData &&
    deliveryDaysData.toArray().sort((a, b) => moment.utc(a.value).diff(moment.utc(b.value)))

  let chosen = false
  // No entry for newDate means no genuinely valid delivery day was resolved for it (e.g. a failed
  // reload left temp.date/slotId unset) - defaulting to true here would keep "Choose recipes"
  // enabled for a date that was never actually validated.
  let hasActiveSlotsForSelectedDate = false

  if (slots[newDate]) {
    hasActiveSlotsForSelectedDate = slots[newDate].some((slot) => !slot.disabled)
    const slot = slots[newDate].filter((sl) => sl.value === tempSlotId)
    chosen = slot.length > 0
  }

  return {
    slots,
    deliveryDays,
    chosen,
    hasOrders,
    hasEmptyOrders,
    hasFullOrders,
    subLabelClassName,
    hasActiveSlotsForSelectedDate,
  }
}
